import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import { signOutUser, useAuth } from '../../auth/firebaseAuth';
import { User, Landlord, Renter, PropertyAgent } from '../../types/userTypes';
import { serverTimestamp } from 'firebase/firestore';
import BrowseListingsPage from '../browse-listings/BrowseListingsPage';
import {
  deactivateAccount,
  insertDocument,
  updateDocument,
  updateUserInListings,
} from '../../utility/firebaseHelpers';
import { uploadFile, deleteImage } from '../../utility/firebaseStorageHelpers';
import { useNavigate } from 'react-router-dom';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import toast from 'react-hot-toast';
import {
  ProfileForm,
  ProfileHeader,
  CommunicationPreferences,
  ListingPreferences,
  AccountManagement,
  AccountActionModals,
} from './components';

const ProfilePage: React.FC = () => {
  const { currentUser, userProfile, loading } = useAuth();
  const [profile, setProfile] = useState<User | Landlord | Renter | PropertyAgent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  // Add these state variables inside your component
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const analytics = useAnalyticsWithConsent();

  analytics.trackPixelPageView({
    email: currentUser?.email || undefined,
    externalId: currentUser?.uid,
  });

  const [newProfile, setNewProfile] = useState<Partial<Landlord | Renter | PropertyAgent>>({
    role: 'renter',
    name: '',
    email: currentUser?.email || '',
    phone: '',
    bio: '',
    communicationPreferences: {
      emailOffers: false,
      pushNotifications: false,
    },
    listingPreferences: {
      showEmail: false,
      showMobile: false,
    },
  });

  useEffect(() => {
    if (userProfile) {
      setProfile(userProfile);
    } else if (currentUser) {
      setIsCreatingProfile(true);
    }
  }, [userProfile, currentUser]);

  useEffect(() => {
    return () => {
      if (previewPhotoUrl) {
        URL.revokeObjectURL(previewPhotoUrl);
      }
    };
  }, [previewPhotoUrl]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (!currentUser) {
    return <BrowseListingsPage />;
  }

  const handleDeactivateAccount = async () => {
    setIsProcessing(true);
    try {
      if (!currentUser?.uid) {
        throw new Error('No user ID found');
      }
      await deactivateAccount(currentUser.uid);
      signOutUser();
      navigate('/');
    } catch (error) {
      console.error('Error deactivating account:', error);
    } finally {
      setIsProcessing(false);
      setIsDeactivateModalOpen(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsProcessing(true);
    try {
      if (!currentUser?.uid) {
        throw new Error('No user ID found');
      }

      // Get the ID token for authentication
      const idToken = await currentUser.getIdToken();

      // Call the Cloud Function to delete the account (has admin privileges)
      const response = await fetch(
        'https://europe-west1-rentsmart-6e31b.cloudfunctions.net/deleteUserAccount',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: currentUser.uid,
            idToken: idToken,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete account');
      }

      // Track account deletion
      analytics.trackInteraction('account_deleted', {
        user_id: currentUser.uid,
        timestamp: new Date().toISOString(),
      });

      // Sign out the user and redirect to home
      await signOutUser();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(
        `Failed to delete account: ${error instanceof Error ? error.message : 'Unknown error'}`,
        {
          duration: 5000,
          position: 'top-center',
        }
      );
      setIsProcessing(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleSave = async () => {
    if (!profile && !isCreatingProfile) {
      console.error('Profile is not loaded and not in creation mode.');
      return;
    }

    setIsUploading(true);
    try {
      let photoUrl = profile?.photoUrl || '';

      if (selectedPhoto) {
        const path = `profile-photos/${currentUser.uid}/${selectedPhoto.name}`;
        const newPhotoUrl = await uploadFile(selectedPhoto, path);

        if (profile?.photoUrl) {
          await deleteImage(profile.photoUrl);
        }

        photoUrl = newPhotoUrl;
      }

      let updatedProfile: User;

      if (isCreatingProfile) {
        const profileData: Landlord | Renter | PropertyAgent = {
          ...newProfile,
          id: currentUser.uid,
          accountCreationDate: serverTimestamp(),
          photoUrl: photoUrl,
          listingPreferences: {
            showEmail: newProfile.listingPreferences?.showEmail || false,
            showMobile: newProfile.listingPreferences?.showMobile || false,
          },
        } as Landlord | Renter | PropertyAgent;

        await insertDocument<User>('users', profileData as User, currentUser.uid);
        updatedProfile = profileData as User;
        setProfile(profileData);
        setIsCreatingProfile(false);
      } else {
        if (profile) {
          profile.photoUrl = photoUrl;
          await updateDocument<User>('users', currentUser.uid, profile as User);
          updatedProfile = { ...profile } as User;
          setProfile({ ...profile } as User);
        } else {
          throw new Error('Profile is undefined');
        }
      }

      // Update user data in all associated listings
      await updateUserInListings(currentUser.uid, updatedProfile);

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsUploading(false);
      if (previewPhotoUrl) {
        URL.revokeObjectURL(previewPhotoUrl);
      }
      setPreviewPhotoUrl(null);
      setSelectedPhoto(null);
    }
  };

  const handleCancel = () => {
    if (previewPhotoUrl) {
      URL.revokeObjectURL(previewPhotoUrl);
    }
    setPreviewPhotoUrl(null);
    setSelectedPhoto(null);
    setIsEditing(false);
    if (isCreatingProfile) {
      setIsCreatingProfile(false);
    }
  };

  const handlePreferenceChange = (preference: 'emailOffers' | 'pushNotifications') => {
    if (profile) {
      setProfile({
        ...profile,
        communicationPreferences: {
          ...profile.communicationPreferences,
          [preference]: !profile.communicationPreferences[preference],
        },
      });
    } else if (isCreatingProfile) {
      setNewProfile((prev) => ({
        ...prev,
        communicationPreferences: {
          ...(prev.communicationPreferences ?? {
            emailOffers: false,
            pushNotifications: false,
          }),
          [preference]: !(prev.communicationPreferences?.[preference] ?? false),
        },
      }));
    }
  };

  const handleListingPreferenceChange = (preference: 'showEmail' | 'showMobile') => {
    if (isCreatingProfile) {
      // For new profile creation
      setNewProfile((prev) => ({
        ...prev,
        listingPreferences: {
          ...(prev.listingPreferences ?? {
            showEmail: false,
            showMobile: false,
          }),
          [preference]: !(prev.listingPreferences?.[preference] ?? false),
        },
      }));
    } else if (profile) {
      // For existing profile
      setProfile({
        ...profile,
        listingPreferences: {
          ...(profile.listingPreferences ?? {
            showEmail: false,
            showMobile: false,
          }),
          [preference]: !(profile.listingPreferences?.[preference] ?? false),
        },
      });
    }
  };

  const handlePhotoChange = (file: File) => {
    setSelectedPhoto(file);

    if (previewPhotoUrl) {
      URL.revokeObjectURL(previewPhotoUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setPreviewPhotoUrl(previewUrl);
  };

  const handleProfileFieldChange = (field: string, value: any) => {
    if (isCreatingProfile) {
      setNewProfile((prev) => ({ ...prev, [field]: value }));
    } else if (profile) {
      setProfile({ ...profile, [field]: value });
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const renderProfileForm = () => (
    <ProfileForm
      profile={isCreatingProfile ? newProfile : profile || {}}
      isCreatingProfile={isCreatingProfile}
      isUploading={isUploading}
      onProfileChange={handleProfileFieldChange}
      onSave={handleSave}
      onCancel={handleCancel}
      profilePhoto={{
        photoUrl: profile?.photoUrl,
        previewPhotoUrl: previewPhotoUrl || undefined,
        onPhotoChange: handlePhotoChange,
      }}
      communicationPreferences={{
        preferences: isCreatingProfile
          ? newProfile.communicationPreferences || {
              emailOffers: false,
              pushNotifications: false,
            }
          : profile?.communicationPreferences || {
              emailOffers: false,
              pushNotifications: false,
            },
        onPreferenceChange: handlePreferenceChange,
      }}
      listingPreferences={{
        preferences: isCreatingProfile
          ? newProfile.listingPreferences || {
              showEmail: false,
              showMobile: false,
            }
          : profile?.listingPreferences || {
              showEmail: false,
              showMobile: false,
            },
        onPreferenceChange: handleListingPreferenceChange,
      }}
    />
  );

  const renderExistingProfile = () => {
    if (!profile) {
      return <div className="text-center text-gray-500">Loading your profile...</div>;
    }

    return (
      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="p-4 md:p-8">
          <ProfileHeader profile={profile} currentUser={currentUser} />

          {/* Communication Preferences */}
          <CommunicationPreferences
            preferences={profile.communicationPreferences}
            onPreferenceChange={handlePreferenceChange}
            isEditing={false}
          />

          {/* Listing Preferences */}
          <ListingPreferences
            preferences={
              profile.listingPreferences || {
                showEmail: false,
                showMobile: false,
              }
            }
            onPreferenceChange={handleListingPreferenceChange}
            isEditing={false}
          />

          {/* Account Management */}
          <AccountManagement
            onDeactivateAccount={() => setIsDeactivateModalOpen(true)}
            onDeleteAccount={() => setIsDeleteModalOpen(true)}
          />

          {/* Action Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleEdit}
              className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-all duration-300 w-full md:w-auto text-sm md:text-base"
            >
              Edit Profile
            </button>
          </div>
        </div>

        {/* Account Action Modals */}
        <AccountActionModals
          isDeleteModalOpen={isDeleteModalOpen}
          isDeactivateModalOpen={isDeactivateModalOpen}
          isProcessing={isProcessing}
          onCloseDeleteModal={() => setIsDeleteModalOpen(false)}
          onCloseDeactivateModal={() => setIsDeactivateModalOpen(false)}
          onConfirmDelete={handleDeleteAccount}
          onConfirmDeactivate={handleDeactivateAccount}
        />
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl md:text-3xl font-extrabold mb-6 text-gray-800 text-center md:text-left">
        My Profile
      </h1>
      {isCreatingProfile || isEditing ? renderProfileForm() : renderExistingProfile()}
    </div>
  );
};

export default ProfilePage;
